import React, { useEffect, useReducer } from 'react';

import { usePrevious } from '@zapier/common-hooks';
import { Spacer, Text } from '@zapier/design-system';

import HelpBox from 'app/developer-v3/components/HelpBox';
import Link from 'app/common/components/Link';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import PartnerSandboxAlert from 'app/developer-v3/components/PartnerSandboxAlert';
import { GET_HELP_PATH } from 'app/developer-v3/teams/team-technical-support-operations/get-help';
import { MIGRATION_TYPES } from 'app/developer-v3/components/UserMigration';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import VersionModals from 'app/developer-v3/components/VersionModals';
import {
  BASE_DOC_URL_VB,
  DEPLOYMENT_STATUSES,
} from 'app/developer-v3/constants';
import {
  TABS as SIDEBAR_RIGHT_TABS,
  VERSIONING_SECTION_IDS,
  useSidebarRight,
} from 'app/developer-v3/components/SidebarRight';
import {
  CurrentVersionReadOnlyReason,
  useActiveProfile,
  useCurrentVersionReadOnlyReason,
  useVersionLifecycle,
} from 'app/developer-v3/hooks';
import {
  VersionLifecycleStatus,
  AppStatusName,
} from 'app/developer-v3/types/statuses';

import VersionsTable from './VersionsTable';

import { isLabeledVersion } from 'app/developer-v3/utils/version';
import type { ValidationResultSetWrapper } from 'app/developer-v3/types';
import type { SemVer } from 'app/developer-v3/types/version';
import type { Version } from './types';

type Props = {
  integrationId: number;
  integrationStatus: AppStatusName;
  isLifecycleRequestFailed: boolean;
  isLifecycleRequestStarted: boolean;
  isLifecycleRequestSuccessful: boolean;
  isLoading: boolean;
  isPublished: boolean;
  isReadyToActivate: boolean;
  lifecycleErrorMessage: string | ValidationResultSetWrapper;
  onClone: (
    integrationId: number,
    fromVersion: SemVer,
    toVersion: SemVer
  ) => void;
  onDelete: (integrationId: number, versionToDelete?: SemVer) => void;
  onDeprecate: (
    integrationId: number,
    versionNumber: SemVer,
    date: string,
    reason: string
  ) => void;
  onLegacy: (integrationId: number, versionNumber: SemVer) => void;
  refreshVersionList: () => void;
  resetAppStatus: () => void;
  resetLifecycle: () => void;
  routeTo: (url: string) => void;
  versions: Array<Version>;
  versionsActive: Array<Version>;
  viewAppVersion: (appVersion: SemVer) => void;
};

type State = {
  deprecateDate: string;
  deprecationReason: string;
  deprecationReasonComment: string;
  fromVersion?: SemVer;
  toVersion: SemVer;
  visibleModal?: string;
};

const VERSION_MODALS = Object.freeze({
  clone: 'clone',
  deprecate: 'deprecate',
  legacy: 'legacy',
});

const initialState = {
  deprecateDate: '',
  deprecationReason: '',
  deprecationReasonComment: '',
  migrateType: MIGRATION_TYPES.percentage,
  migratePercentage: 100,
  toVersion: '',
  visibleModal: undefined,
};

function VersionsPage({
  integrationId,
  integrationStatus,
  isLifecycleRequestFailed,
  isLifecycleRequestStarted,
  isLifecycleRequestSuccessful,
  isLoading,
  isPublished,
  isReadyToActivate,
  lifecycleErrorMessage,
  onClone,
  onDelete,
  onDeprecate,
  onLegacy,
  refreshVersionList,
  resetAppStatus,
  resetLifecycle,
  routeTo,
  versions,
  versionsActive,
  viewAppVersion,
}: Props) {
  const [state, setState] = useReducer(
    (prev: State, update: Partial<State>) => ({ ...prev, ...update }),
    { fromVersion: undefined, ...initialState }
  );

  const {
    deprecateDate,
    deprecationReason,
    deprecationReasonComment,
    fromVersion,
    toVersion,
    visibleModal,
  } = state;

  const closeModal = () => setState(initialState);

  const { activeProfile } = useActiveProfile();

  const versionLifecycle = useVersionLifecycle(integrationId);
  const wasLifecyclePending = usePrevious(versionLifecycle.isPending);

  useEffect(() => {
    if (isReadyToActivate && visibleModal) {
      closeModal();
      resetAppStatus();
    }
  }, [isReadyToActivate, resetAppStatus, visibleModal]);

  // We want to run these actions on the on the first render
  // to ensure any other devs on the team see the most precise
  // state of the integration's versions. This is because we should
  // already have versions in cache, but lifecycle change could
  // have been run by another team member in that time.
  useEffect(() => {
    refreshVersionList();
    resetAppStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (wasLifecyclePending && versionLifecycle.isDone) {
      refreshVersionList();
    }
  }, [refreshVersionList, versionLifecycle.isDone, wasLifecyclePending]);

  const { update } = useSidebarRight();
  const openSidebarTo = {
    migrate(options: { fromVersion: string }) {
      update({
        tabActive: SIDEBAR_RIGHT_TABS.VERSIONING,
        activeSubsection: VERSIONING_SECTION_IDS.MIGRATE,
        sectionContext: options,
      });
    },
    promote(options: { changelog?: string; version: string }) {
      update({
        tabActive: SIDEBAR_RIGHT_TABS.VERSIONING,
        activeSubsection: VERSIONING_SECTION_IDS.PROMOTE,
        sectionContext: options,
      });
    },
  };

  const setToVersion = option => {
    setState({ toVersion: option.value });
  };

  const setFromVersion = option => {
    setState({ fromVersion: option.value });
  };

  const showModal = modal => setState({ visibleModal: modal });

  const closeStatusModal = () => {
    resetLifecycle();
    resetAppStatus();
    closeModal();
  };

  const clone = () => {
    onClone(integrationId, fromVersion, toVersion);
  };

  const deleteVersion = versionToDelete => {
    if (versions.length === 1) {
      onDelete(integrationId);
    } else {
      onDelete(integrationId, versionToDelete);
    }
  };

  const deprecate = () => {
    const finalReason =
      deprecationReason === 'other'
        ? `other: ${deprecationReasonComment}`
        : deprecationReason;
    onDeprecate(integrationId, fromVersion, deprecateDate, finalReason);
  };

  const setAsLegacy = () => {
    onLegacy(integrationId, fromVersion);
  };

  // could instead do a combined migrate target?
  // only want to set one of user or percent, need to unset the other
  // so like, could set migrateMode and a value

  const setDeprecateDate = value => setState({ deprecateDate: value });
  const setDeprecationReason = value => setState({ deprecationReason: value });
  const setDeprecationReasonComment = value =>
    setState({ deprecationReasonComment: value });

  const isLifecycleRequestFinished =
    isLifecycleRequestSuccessful || isLifecycleRequestFailed;
  const waitingOnRequest =
    isLifecycleRequestStarted && !isLifecycleRequestFinished;

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();

  const getMenuItems = (version: Version) => {
    // there are different sets of menu items depending on the status of a version
    // unlike the status labels, this treats all deprecated versions the same, regardless of the date (similar to server)

    /** TEMP CHECKLIST
     * prod:
     *  view
     *  clone
     *  migrate
     *
     * private:
     *  edit
     *  clone
     *  migrate
     *  promote
     *  deprecate
     *  delete
     *
     * deprecated
     *  edit
     *  clone
     *  migrate
     *  delete
     */

    const menuItems = [];
    const isVersionLabeled = isLabeledVersion(version.version);

    if (version.deployment === DEPLOYMENT_STATUSES.production) {
      menuItems.push({
        label: 'View',
        value: 'view',
        onChoose: () => viewAppVersion(version.version),
      });
    } else {
      menuItems.push({
        label: 'Edit',
        value: 'edit',
        onChoose: () => viewAppVersion(version.version),
      });
    }

    if (
      ![CurrentVersionReadOnlyReason.PENDING_VERSION_LIFECYCLE].includes(
        currentVersionReadOnlyReason
      )
    ) {
      menuItems.push({
        label: 'Clone',
        value: 'clone',
        onChoose: () => {
          setFromVersion({ value: version.version });
          showModal(VERSION_MODALS.clone);
        },
      });

      if (!isVersionLabeled) {
        menuItems.push({
          label: 'Migrate',
          value: 'migrate',
          onChoose: () => {
            // Getting this from props because tests use
            // stale value when destructuring
            openSidebarTo.migrate({ fromVersion: version.version });
          },
        });
      }

      if (
        isPublished &&
        version.deployment === DEPLOYMENT_STATUSES.private &&
        !isVersionLabeled
      ) {
        menuItems.push({
          label: 'Promote',
          value: 'promote',
          onChoose: async () => {
            openSidebarTo.promote({
              changelog: version.changelog,
              version: version.version,
            });
          },
        });
      }
      if (version.deployment === DEPLOYMENT_STATUSES.private) {
        // The app will be in a bad state if we deprecate the only remaining version.
        // Django filters out deprecated versions when looking up `latest_version`.
        // https://github.com/zapier/zapier/tree/develop/web/backend/zapier/app_platform/developer_cli/models.py#L820-L840
        // Without latest version, the app won't load at all.
        if (versionsActive.length > 1) {
          menuItems.push({
            label: 'Deprecate',
            value: 'deprecate',
            onChoose: () => {
              setFromVersion({ value: version.version });
              showModal(VERSION_MODALS.deprecate);
            },
          });

          // Only show "Set as Legacy" if the version is not already legacy
          if (version.lifecycle.status !== VersionLifecycleStatus.legacy) {
            menuItems.push({
              label: 'Set as Legacy',
              value: 'legacy',
              onChoose: () => {
                setFromVersion({ value: version.version });
                showModal(VERSION_MODALS.legacy);
              },
            });
          }
        }
      }
      // only show delete for non-production versions, but not for deprecating versions (future deprecation date)
      if (
        version.deployment !== DEPLOYMENT_STATUSES.production &&
        (version.deployment !== DEPLOYMENT_STATUSES.deprecated ||
          Date.parse(version.deprecationDate) <= Date.now())
      ) {
        menuItems.push({
          label: 'Delete',
          value: 'delete',
          isConfirmationRequired: true,
          onChoose: () => {
            deleteVersion(version.version);
          },
          modalTextContent: 'Are you sure you want to delete this version?',
          modalTitle: `Delete version ${version.version}`,
        });
      }
    }

    return menuItems;
  };

  return (
    <ManagerLayout title="Versions">
      {/* MODALS */}
      {visibleModal === VERSION_MODALS.clone &&
        (isLifecycleRequestFinished ? (
          <VersionModals.CloneFinished
            errors={lifecycleErrorMessage}
            isFailure={isLifecycleRequestFailed}
            isSuccess={isLifecycleRequestSuccessful}
            onClose={() => {
              closeStatusModal();
              refreshVersionList();
            }}
            onConfirm={() => {
              closeStatusModal();
              if (isLifecycleRequestSuccessful) {
                viewAppVersion(toVersion);
              } else {
                // route to docs
                routeTo(`${ZAPIER_APP_BASE_URL}${GET_HELP_PATH}`);
              }
            }}
            toVersion={toVersion}
          />
        ) : (
          <VersionModals.Clone
            fromVersion={fromVersion}
            isSaving={waitingOnRequest}
            onClose={closeModal}
            onConfirm={clone}
            onSetFromVersion={setFromVersion}
            onSetToVersion={setToVersion}
            toVersion={toVersion}
            versions={versions}
          />
        ))}
      {visibleModal === VERSION_MODALS.deprecate &&
        (isLifecycleRequestFinished ? (
          <VersionModals.Status
            cancelButtonLabel="Close"
            confirmButtonLabel={!isLifecycleRequestSuccessful ? 'Get Help' : ''}
            description={
              isLifecycleRequestSuccessful
                ? 'This version has been successfully deprecated, and will show as a legacy integration in Zaps. Once no users are using this version, you can delete it.'
                : 'This may be something you can fix!'
            }
            heading={
              isLifecycleRequestSuccessful
                ? 'Deprecated Successfully'
                : 'There was a Problem'
            }
            isSuccess={isLifecycleRequestSuccessful}
            onClose={() => {
              closeStatusModal();
              refreshVersionList();
            }}
            onConfirm={() => {
              closeStatusModal();
              if (!isLifecycleRequestSuccessful) {
                // route to docs
                routeTo(`${ZAPIER_APP_BASE_URL}${GET_HELP_PATH}`);
              }
            }}
          />
        ) : (
          <VersionModals.Deprecate
            date={deprecateDate}
            isSaving={waitingOnRequest}
            onClose={closeModal}
            onConfirm={deprecate}
            onSetDate={setDeprecateDate}
            onSetReason={setDeprecationReason}
            onSetReasonComment={setDeprecationReasonComment}
            reason={deprecationReason}
            reasonComment={deprecationReasonComment}
            version={fromVersion || ''}
          />
        ))}
      {visibleModal === VERSION_MODALS.legacy &&
        (isLifecycleRequestFinished ? (
          <VersionModals.Status
            cancelButtonLabel="Close"
            confirmButtonLabel={!isLifecycleRequestSuccessful ? 'Get Help' : ''}
            description={
              isLifecycleRequestSuccessful
                ? 'This version has been successfully marked as legacy. Users will see that this version is no longer recommended.'
                : 'This may be something you can fix!'
            }
            heading={
              isLifecycleRequestSuccessful
                ? 'Set as Legacy Successfully'
                : 'There was a Problem'
            }
            isSuccess={isLifecycleRequestSuccessful}
            onClose={() => {
              closeStatusModal();
              refreshVersionList();
            }}
            onConfirm={() => {
              closeStatusModal();
              if (!isLifecycleRequestSuccessful) {
                // route to docs
                routeTo(`${ZAPIER_APP_BASE_URL}${GET_HELP_PATH}`);
              }
            }}
          />
        ) : (
          <VersionModals.Legacy
            isSaving={waitingOnRequest}
            onClose={closeModal}
            onConfirm={setAsLegacy}
            version={fromVersion || ''}
          />
        ))}
      {/* The actual page starts here */}
      <HelpBox
        description={
          <Text type="Body1">
            You can create multiple versions of your integration to test and
            build new features without interrupting existing users. Before
            making updates, it's important to consider the potential{' '}
            <Link
              href={`${BASE_DOC_URL_VB}/manage/planning-changes`}
              target="_blank"
            >
              impact on user migration and existing Zaps.
            </Link>{' '}
            When you are ready to create a new version,{' '}
            <Link href={`${BASE_DOC_URL_VB}/manage/versions`} target="_blank">
              this guide
            </Link>{' '}
            will cover the steps for Cloning, Promoting, Migrating, and
            Deprecating integration versions.
          </Text>
        }
        heading="Manage versions of your integration"
      />
      <Spacer height={20} />
      {integrationStatus === 'public' && (
        <PartnerSandboxAlert
          email={activeProfile.email}
          integrationId={integrationId}
          persistId="partner-sandbox-alert-versions-page"
        />
      )}
      <Spacer height={20} />
      {versions.length ? (
        <VersionsTable
          getMenuItems={getMenuItems}
          integrationId={integrationId}
          integrationStatus={integrationStatus}
          isLoading={isLoading}
          versions={versions}
          viewAppVersion={viewAppVersion}
        />
      ) : (
        <p>No Data</p>
      )}
    </ManagerLayout>
  );
}

export default VersionsPage;
